import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import mondaySdk from "monday-sdk-js";
import { tokenStore } from "../../utils/security/tokenStore";

const monday = mondaySdk();

interface AccessTokenState {
  token: string | null;
  loading: boolean;
  error: string | null;
}

const initialState: AccessTokenState = {
  token: null,
  loading: false,
  error: null,
};

/**
 * Fetches the Monday session token for initial load.
 * Uses in-memory cached token if still valid.
 * Token refresh during API calls is handled by axios interceptors in utils/requests/index.ts
 */
export const fetchAccessToken = createAsyncThunk<string, void>("accessToken/fetchToken", async (_, { rejectWithValue }) => {
  try {
    const cachedToken = tokenStore.getToken();
    if (cachedToken && tokenStore.hasValidToken()) {
      return cachedToken;
    }

    const response = await monday.get("sessionToken");
    const sessionToken: string = response.data;

    if (!sessionToken) {
      throw new Error("Failed to get session token from Monday");
    }

    tokenStore.setToken(sessionToken);
    return sessionToken;
  } catch (error: any) {
    tokenStore.clearToken();
    return rejectWithValue(error.message || "Failed to fetch session token");
  }
});

const accessTokenSlice = createSlice({
  name: "accessToken",
  initialState,
  reducers: {
    resetTokenState: (state) => {
      state.token = null;
      state.loading = false;
      state.error = null;
      tokenStore.clearToken();
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAccessToken.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAccessToken.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.token = action.payload;
        state.error = null;
      })
      .addCase(fetchAccessToken.rejected, (state, action) => {
        state.loading = false;
        state.token = null;
        state.error = action.payload as string;
      });
  },
});

export const { resetTokenState } = accessTokenSlice.actions;
export default accessTokenSlice.reducer;
